import React from 'react';
import UserAvatar from '../common/UserAvatar';
import { TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ImageCard from '../common/ImageCard';
import SmartImage from '../common/SmartImage';
import { PLANET_BACKGROUND_MAP } from '../../utils/planetBackgrounds';

const ExploreCard = ({ 
  id,
  title, 
  description, 
  coverImage, 
  author, 
  stats,
  tags = [],
  onClick,
  className = '',
  imageHeight = 'h-full',
  layout = 'vertical', // 'vertical' or 'horizontal'
  status // 'draft', 'published', etc.
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // Prevent navigation if status is not published (optional, based on requirements)
    // If we want to allow editing drafts, we might keep navigation or handle it differently
    if (onClick) {
      onClick();
    } else {
      navigate(`/explore/proposals/${id}`); 
    }
  };

  // Find the planet image based on tags
  const planetTag = tags.find(tag => PLANET_BACKGROUND_MAP[tag]);
  const planetImage = planetTag ? PLANET_BACKGROUND_MAP[planetTag] : null;

  if (layout === 'horizontal') {
    return (
      <div 
        className={`bg-white rounded-lg overflow-hidden cursor-pointer transition-all shadow-md hover:shadow-lg hover:-translate-y-1 group flex h-[268px] w-[416px] flex-shrink-0 relative ${className}`}
        onClick={handleClick}
      >
        {/* Status Mask */}
        {status && status !== 'published' && status !== 'draft' && (
          <div className="absolute inset-0 bg-black/50 z-20 flex items-center justify-center rounded-lg">
            <div className="text-white text-base font-normal">
              已发布
            </div>
          </div>
        )}

        {/* Cover Image - Fixed width based on design (196px/416px ~ 47%) */}
        <div className="w-[196px] h-full relative overflow-hidden bg-gray-100 flex-shrink-0 rounded-lg shadow-[2px_0_10px_rgba(0,0,0,0.3)] z-20">
          <SmartImage
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          
          {/* Draft Badge or Tags */}
          {status === 'draft' ? (
            <div className="absolute top-3 left-3 bg-[#BBFD3B] text-black text-xs font-black px-3 py-1 rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.16)] z-10 tracking-tighter">
              草稿
            </div>
          ) : tags.length > 0 && (
            <div 
              className={
                tags[0] === 'ai_competition'
                  ? "absolute top-[238px] left-[38px] w-[120px] h-[18px] bg-white rounded-[40px] flex items-center justify-center text-xs font-bold text-black shadow-sm"
                  : "absolute top-3 left-3 text-black text-base px-2 py-0.5 rounded shadow-[0_4px_8px_rgba(0,0,0,0.16)] bg-[#F0FF31] font-black -skew-x-12 tracking-tighter"
              }
              style={tags[0] === 'ai_competition' ? {} : { WebkitTextStroke: '0.5px black' }}
            >
              {tags[0] === 'ai_competition' ? '燕缘·滴水湖站' : tags[0]}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 bg-white flex flex-col justify-between flex-1 min-w-0 h-full relative overflow-hidden">
          <div className="flex flex-col gap-2 relative z-10">
            <h3 className="text-lg font-bold text-[var(--color-text-on-light-base)] leading-tight line-clamp-2" title={title}>
              {title || '无标题'}
            </h3>
            
            <p className="text-sm text-[var(--color-text-on-light-tertiary)] line-clamp-4 leading-relaxed">
              {description || '暂无描述'}
            </p>
          </div>

          {/* Planet Image Background - Positioned at bottom right */}
          {planetImage && (
            <img 
              src={planetImage} 
              alt="Planet" 
              className="absolute -bottom-10 -right-10 w-28 h-28 object-contain opacity-100 pointer-events-none"
            />
          )}

          {/* Footer: Author */}
          <div className="flex items-center justify-between mt-auto pt-4 relative z-10">
            <div className="flex items-center gap-2">
              <UserAvatar size={24} user={author} />
              <span className="text-sm text-[var(--color-text-on-light-secondary)] font-medium truncate max-w-[100px]">
                {author?.name || 'Unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderBadge = () => {
    if (status === 'draft') {
      return (
        <div className="absolute top-3 left-3 bg-[#BBFD3B] text-black text-xs font-black px-3 py-1 rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.16)] z-10 tracking-tighter">
          草稿
        </div>
      );
    }

    const hasMate = Array.isArray(tags) && tags.some(t => String(t).includes('找队友'));
    
    if (!hasMate) return null;

    return (
      <div className="absolute top-3 left-3 w-6 h-6 rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.16)] bg-[#F0FF31] text-black flex items-center justify-center">
        <TeamOutlined className="text-xs" />
      </div>
    );
  };

  return (
    <ImageCard
      title={title}
      description={description}
      coverImage={coverImage}
      author={author}
      likes={stats?.likes}
      is_liked={stats?.is_liked}
      onClick={handleClick}
      className={className}
      renderBadge={renderBadge}
    />
  );
};

export default ExploreCard;
